import { useEffect, useCallback } from 'react';

// Enhanced Microsoft Clarity Analytics Hook for BiBi
export const useAnalytics = () => {
    useEffect(() => {
        // Initialize scroll depth tracking
        let maxScrollDepth = 0;
        let timeOnPageStart = Date.now();

        const trackScrollDepth = () => {
            const scrollDepth = Math.round(
                (window.scrollY + window.innerHeight) / document.documentElement.scrollHeight * 100
            );

            if (scrollDepth > maxScrollDepth && scrollDepth % 25 === 0) {
                maxScrollDepth = scrollDepth;
                trackEvent('scroll_depth', { percentage: scrollDepth });
            }
        };

        const trackTimeOnPage = () => {
            const timeSpent = Math.round((Date.now() - timeOnPageStart) / 1000);
            if (timeSpent % 30 === 0 && timeSpent > 0) { // Every 30 seconds
                trackEvent('time_on_page', { seconds: timeSpent });
            }
        };

        // Set up event listeners
        window.addEventListener('scroll', trackScrollDepth, { passive: true });
        const timeInterval = setInterval(trackTimeOnPage, 30000); // 30 second intervals

        // Track page load
        trackEvent('page_view', {
            path: window.location.pathname,
            referrer: document.referrer,
            timestamp: new Date().toISOString()
        });

        return () => {
            window.removeEventListener('scroll', trackScrollDepth);
            clearInterval(timeInterval);

            // Track final time on page
            const finalTime = Math.round((Date.now() - timeOnPageStart) / 1000);
            trackEvent('session_end', {
                totalTimeSeconds: finalTime,
                maxScrollDepth: maxScrollDepth
            });
        };
    }, []);

    // Enhanced event tracking with better error handling
    const trackEvent = useCallback((eventName: string, customData?: Record<string, any>) => {
        try {
            // Always log in development
            if (import.meta.env.DEV) {
                console.log('📊 Analytics Event:', eventName, customData);
            }

            // Send to Microsoft Clarity
            if (typeof window !== 'undefined' && window.clarity) {
                window.clarity('event', eventName, customData || {});
            }

            // Future: Could add other analytics providers here
            // e.g., Google Analytics, Mixpanel, etc.

        } catch (error) {
            console.warn('Analytics tracking failed:', error);
        }
    }, []);

    // Comprehensive BiBi-specific tracking
    const trackUserAction = {
        // Landing page hero interactions
        heroCtaClick: (ctaType: 'main' | 'secondary') =>
            trackEvent('hero_cta_click', { cta_type: ctaType }),

        heroPathSelect: (path: 'senior' | 'volunteer' | 'professional') =>
            trackEvent('hero_path_select', { path }),

        // Service card interactions
        serviceCardExpand: (service: string) =>
            trackEvent('service_card_expand', { service }),

        serviceCardCollapse: (service: string) =>
            trackEvent('service_card_collapse', { service }),

        serviceCardClick: (service: string, area: string) =>
            trackEvent('service_card_click', { service, area }),

        // Early interest form flow
        earlyInterestStart: () =>
            trackEvent('early_interest_start'),

        interestTypeSelect: (type: string) =>
            trackEvent('interest_type_select', { interest_type: type }),

        formFieldFocus: (fieldName: string, formType: string) =>
            trackEvent('form_field_focus', { field: fieldName, form: formType }),

        formFieldComplete: (fieldName: string, formType: string) =>
            trackEvent('form_field_complete', { field: fieldName, form: formType }),

        formValidationError: (fieldName: string, errorType: string, formType: string) =>
            trackEvent('form_validation_error', {
                field: fieldName,
                error_type: errorType,
                form: formType
            }),

        formSubmitAttempt: (formType: string, data: Record<string, any>) =>
            trackEvent('form_submit_attempt', {
                form: formType,
                ...data
            }),

        formSubmitSuccess: (formType: string, submissionData: Record<string, any>) =>
            trackEvent('form_submit_success', {
                form: formType,
                user_type: submissionData.interestType,
                zip_code: submissionData.zipCode
            }),

        formSubmitError: (formType: string, errorMessage: string) =>
            trackEvent('form_submit_error', {
                form: formType,
                error: errorMessage
            }),

        // Professional path tracking
        professionalCtaClick: (location: string) =>
            trackEvent('professional_cta_click', { location }),

        professionalFormOpen: () =>
            trackEvent('professional_form_open'),

        professionalServiceSelect: (services: string[]) =>
            trackEvent('professional_service_select', { services }),

        // Contact and communication
        contactFormOpen: (source: string) =>
            trackEvent('contact_form_open', { source }),

        contactSubmit: (contactType: string) =>
            trackEvent('contact_submit', { contact_type: contactType }),

        emailClick: (emailType: string) =>
            trackEvent('email_click', { email_type: emailType }),

        phoneClick: () =>
            trackEvent('phone_click'),

        // Navigation and engagement
        backToTop: () =>
            trackEvent('back_to_top_click'),

        menuOpen: () =>
            trackEvent('menu_open'),

        menuItemClick: (item: string) =>
            trackEvent('menu_item_click', { menu_item: item }),

        externalLinkClick: (url: string, context: string) =>
            trackEvent('external_link_click', { url, context }),

        // Content engagement
        faqExpand: (question: string) =>
            trackEvent('faq_expand', { question }),

        testimonialView: (testimonialId: string) =>
            trackEvent('testimonial_view', { testimonial_id: testimonialId }),

        pricingView: () =>
            trackEvent('pricing_section_view'),

        // Error tracking
        apiError: (endpoint: string, errorCode: number, errorMessage: string) =>
            trackEvent('api_error', {
                endpoint,
                error_code: errorCode,
                error_message: errorMessage
            }),

        jsError: (errorMessage: string, filename?: string, lineno?: number) =>
            trackEvent('javascript_error', {
                error_message: errorMessage,
                filename,
                line_number: lineno
            }),

        // User journey milestones
        journeyMilestone: (milestone: string, metadata?: Record<string, any>) =>
            trackEvent('journey_milestone', {
                milestone,
                ...metadata
            }),

        // Custom business events
        businessEvent: (eventName: string, properties: Record<string, any>) =>
            trackEvent(`business_${eventName}`, properties),
    };

    // Utility functions
    const setUserProperties = useCallback((properties: Record<string, any>) => {
        if (typeof window !== 'undefined' && window.clarity) {
            Object.entries(properties).forEach(([key, value]) => {
                window.clarity('set', key, value);
            });
        }
    }, []);

    const identifyUser = useCallback((userId: string, traits?: Record<string, any>) => {
        if (typeof window !== 'undefined' && window.clarity) {
            window.clarity('identify', userId, traits);
        }
    }, []);

    return {
        trackEvent,
        trackUserAction,
        setUserProperties,
        identifyUser
    };
};

// Global Clarity type definitions
declare global {
    interface Window {
        clarity: (action: string, ...args: any[]) => void;
    }
}
